import React, {useRef, useState} from 'react';
import {DndProvider, useDrag, useDrop} from 'react-dnd';
import {HTML5Backend} from 'react-dnd-html5-backend';
import {
    Badge,
    Box,
    Button,
    Card,
    Flex,
    Grid,
    GridItem,
    Heading,
    Image,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Progress,
    SimpleGrid, Spinner,
    Text,
    useDisclosure,
    useToast
} from "@chakra-ui/react";
import {CloseIcon} from "@chakra-ui/icons";
import {deleteData, postData, putData} from "../../services/api";

// Define the item type
const ItemType = 'PICTURE';

// Draggable component for each file
const DraggableFileItem = ({file, index, moveFile, handleRemoveFile}) => {
    const ref = React.useRef(null);

    const [, drop] = useDrop({
        accept: ItemType,
        hover(item) {
            // No need to switch positions during hover, but track the hover index if needed
            item.hoverIndex = index;
        },
    });

    const [{isDragging}, drag, preview] = useDrag({
        type: ItemType,
        item: {index},
        end: (item, monitor) => {
            const didDrop = monitor.didDrop();
            if (didDrop && item.hoverIndex !== undefined && item.hoverIndex !== item.index) {
                // Perform the swap only when the drag ends and the indexes differ
                moveFile(item.index, item.hoverIndex);
            }
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        })
    });

    // useEffect(() => {
    //     preview(getEmptyImage(), { captureDraggingState: true })
    // }, [])

    drag(drop(ref));

    return (
        <GridItem
            ref={ref}
            height={'300px'}
            colSpan={index === 0 ? 2 : 1}
        >
            {!isDragging ? <Card height='100%' overflow='hidden'>
                    {index === 0 ? <Badge bgColor='white' position='absolute' top='5' left='5'>Titelbild</Badge> : ''}
                    <Image
                        height='100%'
                        width='100%'
                        objectFit='cover'
                        key={index}
                        src={file.url}
                        alt={file.name}
                    />
                    <Badge onClick={() => handleRemoveFile(file.id)} bgColor='white'
                           position='absolute' top='5' right='5' cursor='pointer'>
                        <Flex gap='2'>
                            <Text>Entfernen</Text>
                            <CloseIcon color='red'/>
                        </Flex>
                    </Badge>
                </Card> :
                <Card height='100%' bgColor='grey.500'></Card>
            }
        </GridItem>
    );
};

function FileDropAndSort({rentalId, files, setFiles}) {
    const [uploads, setUploads] = useState([]);
    const [progress, setProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);

    const fileInputRef = useRef(null);
    const {isOpen, onOpen, onClose} = useDisclosure();

    const MAX_FILE_SIZE = 7 * 1024 * 1024; // 7 MB in Bytes
    const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
    const MAX_FILES = 30;

    const toast = useToast()

    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    };

    const handleFileChange = async (event) => {
        const selectedFiles = Array.from(event.target.files)
        await handleFilesSelected(selectedFiles);
    };

    const handleFileDrop = async (event) => {
        event.preventDefault(); // Prevent default behavior (like opening files in the browser)
        const droppedFiles = Array.from(event.dataTransfer.files); // Extract files from drop event
        await handleFilesSelected(droppedFiles);
    };
    const handleFilesSelected = async (selectedFiles) => {
        const currentFileCount = files.length;

        if (currentFileCount + selectedFiles.length > MAX_FILES) {
            toast({
                title: `Es können maximal ${MAX_FILES} hochgeladen werden. Aktuell kannst du noch ${MAX_FILES - currentFileCount} Bilder hochladen.`,
                status: 'error',
                isClosable: true,
            });
            return;
        }

        const validFiles = selectedFiles.filter(validateFile);

        const base64Files = await Promise.all(
            validFiles.map(async (file, index) => ({
                id: `${file.name}-${index}`,
                name: file.name,
                base64: await fileToBase64(file),
                original: file,
            }))
        );
        setUploads((prevFiles) => [...prevFiles, ...base64Files]);
    };

    const validateFile = (file) => {
        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
            console.error(`Unsupported file type: ${file.type}`);
            toast({
                title: 'Dieser Dateityp wird leider nicht unterstützt',
                status: 'success',
                isClosable: true,
            })
            return false;
        }
        if (file.size > MAX_FILE_SIZE) {
            console.error(`File size exceeds limit: ${file.name}`);
            toast({
                title: 'Deine Bilder dürfen maximal 7 MB groß sein',
                status: 'success',
                isClosable: true,
            })
            return false;
        }
        return true;
    };

    const moveFile = (dragIndex, hoverIndex) => {
        const updatedFiles = [...files];

        // Swap the two pictures
        const temp = updatedFiles[dragIndex];
        updatedFiles[dragIndex] = updatedFiles[hoverIndex];
        updatedFiles[hoverIndex] = temp;

        setFiles(updatedFiles);
        updatePictures(updatedFiles);
    };

    const handleRemoveUpload = async (index) => {
        setUploads((prevFiles) => prevFiles.filter((_, i) => i !== index));
    }

    const updatePictures = async (pictures) => {
        try {
            await putData('/api/rentals/' + rentalId + '/pictures', {pictures: pictures});
        } catch (error) {
            toast({
                title: 'Es ist ein Fehler aufgetreten: ' + error.message,
                status: 'error',
                isClosable: true,
            })
            return false;
        }
    };

    const handleRemoveFile = async (pictureId) => {
        try {
            setFiles((prevFiles) => prevFiles.filter((file) => file.id !== pictureId));
            await deleteData('/api/rentals/' + rentalId + '/pictures/' + pictureId);
            return true;
        } catch (error) {
            toast({
                title: 'Es ist ein Fehler aufgetreten: ' + error.message,
                status: 'error',
                isClosable: true,
            })
            return false;
        } finally {
            onClose();
        }
    };

    const handleUploadFiles = async () => {
        try {
            setIsUploading(true);

            const formData = new FormData();
            uploads.forEach((upload) => {
                formData.append('pictures[]', upload.original); // Assuming multiple files
            });

            const result = await postData('/api/rentals/' + rentalId + '/pictures', formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
                onUploadProgress: (progressEvent) => {
                    const percentage = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setProgress(percentage);
                },
            });
            setFiles(result);
            toast({
                title: 'Upload erfolgreich!',
                status: 'success',
                isClosable: true,
            });
            return true;
        } catch (error) {
            toast({
                title: 'Es ist ein Fehler aufgetreten: ' + error.message,
                status: 'error',
                isClosable: true,
            })
            return false;
        } finally {
            setIsUploading(false);
            setProgress(0);
            setUploads([]);
            onClose();
        }
    };

    const openFileDialog = () => {
        fileInputRef.current.click(); // Use the ref to programmatically trigger the file input click
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <div>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    accept={ALLOWED_FILE_TYPES.join(',')}
                    style={{display: 'none'}} // Hide the file input element
                />

                {files.length === 0 ?
                    <Card p={5} height='350px'>
                        <Heading mb={5} size='xl'>Füge ein paar Fotos deiner Unterkunft hinzu</Heading>
                        <Text>Lade mindestens 5 Fotos hoch, eher mehr. Urlauber lieben Bilder deiner Unterkunft!</Text>
                        <Button
                            fontSize='sm'
                            variant='brand'
                            fontWeight='500'
                            w='auto'
                            h='50'
                            mt='auto'
                            mb='auto'
                            ml='auto'
                            mr='auto'
                            onClick={onOpen}
                        >
                            Fotos hinzufügen
                        </Button>
                    </Card> :
                    <Card p={5}>
                        <Heading mb={5} size='xl'>Das sind deine Fotos</Heading>
                        <Text>Du kannst die Reihenfolge per Drag & Drop ändern.</Text>
                    </Card>
                }

                <Grid
                    mt={5}
                    mb={5}
                    templateColumns='repeat(2, 1fr)'
                    gap={4}
                >
                    {files.map((file, index) => (
                        <DraggableFileItem
                            key={index}
                            file={file}
                            index={index}
                            moveFile={moveFile}
                            handleRemoveFile={handleRemoveFile}
                        />
                    ))}
                    {files.length < 5 ?
                        <GridItem colSpan='2'>
                            <Text align={'center'}>Lade mindestens 5 Fotos hoch</Text>
                        </GridItem>
                        : ''
                    }
                    {/*<CustomDragLayer files={files} />*/}
                    {files.length > 0 ?
                        <GridItem colSpan='2' justifyContent='center' display='flex'>
                            <Button
                                fontSize='sm'
                                variant='brand'
                                fontWeight='500'
                                w='auto'
                                h='50'
                                onClick={onOpen}
                            >
                                Weitere hinzufügen
                            </Button>
                        </GridItem>
                        : ''
                    }
                </Grid>
                <Modal isOpen={isOpen} onClose={onClose}>
                    <ModalOverlay/>
                    <ModalContent bgColor="white">
                        <ModalHeader display="flex" justifyContent="center" alignItems="center">Bilder
                            hochladen</ModalHeader>
                        <ModalCloseButton/>
                        <ModalBody>
                            {uploads.length ? <SimpleGrid columns={2} spacing={10}> {uploads.map((upload, index) => (
                                    <Card height='180px' width='180px' overflow='hidden' key={index}>
                                        <Image
                                            height='100%'
                                            width='100%'
                                            objectFit='cover'
                                            key={index}
                                            src={upload.base64}
                                            alt={upload.name}
                                        />
                                        <Badge onClick={() => handleRemoveUpload(index)} bgColor='white'
                                               position='absolute' top='5' right='5' cursor='pointer'>
                                            <Flex gap='2'>
                                                <Text>Entfernen</Text>
                                                <CloseIcon color='red'/>
                                            </Flex>
                                        </Badge>
                                    </Card>
                                ))} </SimpleGrid> :
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    borderWidth="2px"
                                    borderRadius="md"
                                    borderStyle="dashed"
                                    borderColor="gray.300"
                                    p={5}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={handleFileDrop}
                                >
                                    <svg height="64px" width="64px" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M41.636 8.404l1.017 7.237 17.579 4.71a5 5 0 0 1 3.587 5.914l-.051.21-6.73 25.114A5.002 5.002 0 0 1 53 55.233V56a5 5 0 0 1-4.783 4.995L48 61H16a5 5 0 0 1-4.995-4.783L11 56V44.013l-1.69.239a5 5 0 0 1-5.612-4.042l-.034-.214L.045 14.25a5 5 0 0 1 4.041-5.612l.215-.035 31.688-4.454a5 5 0 0 1 5.647 4.256zm-20.49 39.373l-.14.131L13 55.914V56a3 3 0 0 0 2.824 2.995L16 59h21.42L25.149 47.812a3 3 0 0 0-4.004-.035zm16.501-9.903l-.139.136-9.417 9.778L40.387 59H48a3 3 0 0 0 2.995-2.824L51 56v-9.561l-9.3-8.556a3 3 0 0 0-4.053-.009zM53 34.614V53.19a3.003 3.003 0 0 0 2.054-1.944l.052-.174 2.475-9.235L53 34.614zM48 27H31.991c-.283.031-.571.032-.862 0H16a3 3 0 0 0-2.995 2.824L13 30v23.084l6.592-6.59a5 5 0 0 1 6.722-.318l.182.159.117.105 9.455-9.817a5 5 0 0 1 6.802-.374l.184.162L51 43.721V30a3 3 0 0 0-2.824-2.995L48 27zm-37 5.548l-5.363 7.118.007.052a3 3 0 0 0 3.388 2.553L11 41.994v-9.446zM25.18 15.954l-.05.169-2.38 8.876h5.336a4 4 0 1 1 6.955 0L48 25.001a5 5 0 0 1 4.995 4.783L53 30v.88l5.284 8.331 3.552-13.253a3 3 0 0 0-1.953-3.624l-.169-.05L28.804 14a3 3 0 0 0-3.623 1.953zM21 31a4 4 0 1 1 0 8 4 4 0 0 1 0-8zm0 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM36.443 6.11l-.175.019-31.69 4.453a3 3 0 0 0-2.572 3.214l.02.175 3.217 22.894 5.833-7.74a5.002 5.002 0 0 1 4.707-4.12L16 25h4.68l2.519-9.395a5 5 0 0 1 5.913-3.587l.21.051 11.232 3.01-.898-6.397a3 3 0 0 0-3.213-2.573zm-6.811 16.395a2 2 0 0 0 1.64 2.496h.593a2 2 0 1 0-2.233-2.496zM10 13a4 4 0 1 1 0 8 4 4 0 0 1 0-8zm0 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"></path>
                                    </svg>
                                    <Heading mt={5} mb={5} size='lg' textAlign="center">Wähle deine Bilder aus</Heading>
                                    <Button
                                        variant='brand'
                                        fontWeight='500'
                                        w='auto'
                                        h='50'
                                        onClick={openFileDialog}
                                    >
                                        Durchsuchen
                                    </Button>
                                </Box>
                            }
                            {isUploading && <Progress value={progress} size="xs" mt={4}/>}
                        </ModalBody>

                        <ModalFooter>
                            <Button variant='ghost' mr={3} onClick={onClose}>
                                Schließen
                            </Button>
                            <Button isDisabled={uploads.length === 0 || isUploading} onClick={handleUploadFiles}
                                    variant='brand' fontWeight='500'>
                                {isUploading ? <Spinner size="sm"/> : 'Hochladen'}
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </div>
        </DndProvider>
    );
}

export default FileDropAndSort;
